import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Grid,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Paper,
  Tooltip,
  TextField,
  InputAdornment,
  IconButton,
  Stack,
  Breadcrumbs,
  Button,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useParams, useNavigate } from 'react-router-dom';
import { alpha , darken, lighten} from '@mui/material/styles';
import { BASEURL ,PRIMARYCOLOR,SECONDARYCOLOR,logoURL,COMPANY } from '../../Constants';
import Nav from './nav';

// icons
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinkIcon from '@mui/icons-material/Link';

/* ---------- helpers ---------- */
const scoreColor = (s) => (s >= 75 ? '#d32f2f' : s >= 50 ? '#f57c00' : '#1976d2');
const trunc = (str, len = 40) => (str?.length > len ? `${str.slice(0, len - 3)}…` : str);
const methodColor = (m) => ({
  GET: '#90caf9',
  POST: '#a5d6a7',
  PUT: '#ffe082',
  DELETE: '#ffab91',
  PATCH: '#ce93d8',
}[m] || '#e0e0e0');

/* ---------- markdown → MUI ---------- */
const mdComponents = {
  h1: (props) => <Typography variant="h5" sx={{ mt: 2, mb: 1, fontWeight: 800 }} {...props} />,
  h2: (props) => <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 800 }} {...props} />,
  p:  (props) => <Typography variant="body2" sx={{ mb: 1.2, lineHeight: 1.7 }} {...props} />,
  li: (props) => (
    <li style={{ marginLeft: 16 }}>
      <Typography component="span" variant="body2" {...props} />
    </li>
  ),
  em: (props) => (
    <Typography component="em" variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }} {...props} />
  ),
  code: ({ inline, ...props }) =>
    inline ? (
      <code
        style={{
          background: '#f5f5f5',
          border: '1px solid #e0e0e0',
          borderRadius: 4,
          padding: '1px 4px',
          fontFamily: 'monospace',
          fontSize: '0.85em',
        }}
        {...props}
      />
    ) : (
      <Box
        component="code"
        sx={{
          display: 'inline-block',
          bgcolor: '#f5f5f5',
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          px: 1.5,
          py: 0.75,
          fontSize: '0.85rem',
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
        }}
        {...props}
      />
    ),
};

/* ---------- main ---------- */
const PentestApiThreatModel = () => {
  const { pentest_id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [endpoints, setEndpoints] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [search, setSearch] = useState('');
  const [methodFilter, setMethodFilter] = useState('ALL');

  // keyboard UX for right list
  const [kbdIndex, setKbdIndex] = useState(0);

  /* fetch once --------------------------------------------------- */
  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) return navigate('/');

    fetch(`${BASEURL}/pentests/api/threat_model?pentest_id=${pentest_id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (!data.success) throw new Error(data.message || 'Load error');

        const apiModel = {
          id: 0,
          method: 'OVERVIEW',
          path: 'API Overview',
          attacks: [],
          md: data.threat_model.api || '',
        };

        const epModels = (data.threat_model.endpoints || []).map((ep, i) => ({
          id: i + 1,
          method: (ep.http_method || 'GET').toUpperCase(),
          path: ep.path || `Endpoint ${i + 1}`,
          attacks: ep.threat_model?.attacks || [],
          md: ep.threat_model?.threat_model || '',
        }));

        setEndpoints([apiModel, ...epModels]);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        navigate('/');
      });
  }, [navigate, pentest_id]);

  const active = useMemo(() => endpoints[activeIdx] || null, [endpoints, activeIdx]);

  /* --- high-score attacks per endpoint -------------------------- */
  const highAttacks = useMemo(
    () => (active?.attacks || []).filter((a) => a.score >= 30).sort((a, b) => b.score - a.score),
    [active]
  );

  /* --- derived stats for KPI cards ------------------------------ */
  const stats = useMemo(() => {
    const eps = endpoints.slice(1);
    const total = eps.length;
    const risky = eps.reduce((acc, ep) => acc + (ep.attacks?.some((a) => a.score >= 75) ? 1 : 0), 0);
    const methods = eps.reduce((m, ep) => ({ ...m, [ep.method]: (m[ep.method] || 0) + 1 }), {});
    return { total, risky, methods };
  }, [endpoints]);

  /* --- aggregated view for API Overview → rows for table -------- */
  const overviewRows = useMemo(() => {
    if (activeIdx !== 0) return [];
    const map = {};
    endpoints.slice(1).forEach((ep) => {
      (ep.attacks || [])
        .filter((a) => a.score >= 30)
        .forEach((a) => {
          if (!map[a.name]) map[a.name] = { name: a.name, count: 0, endpoints: new Set() };
          map[a.name].count += 1;
          map[a.name].endpoints.add(ep.path);
        });
    });
    return Object.values(map)
      .map((x) => ({ ...x, endpoints: Array.from(x.endpoints) }))
      .sort((a, b) => (b.count - a.count) || a.name.localeCompare(b.name));
  }, [activeIdx, endpoints]);

  /* --- filters for endpoint list ------------------------------- */
  const filteredEndpoints = useMemo(() => {
    const q = search.trim().toLowerCase();
    return endpoints.filter((ep, idx) => {
      if (idx === 0) return true; // keep overview
      if (methodFilter !== 'ALL' && ep.method !== methodFilter) return false;
      if (!q) return true;
      return ep.path.toLowerCase().includes(q);
    });
  }, [endpoints, search, methodFilter]);

  // deep-link from overview to endpoint detail
  const goToEndpointPath = (path) => {
    const idx = endpoints.findIndex((e) => e.path === path);
    if (idx > -1) setActiveIdx(idx);
  };

  // keyboard navigation for the right list (↑/↓/Enter)
  const onKeyDownList = (e) => {
    if (!filteredEndpoints.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setKbdIndex((p) => Math.min(p + 1, filteredEndpoints.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setKbdIndex((p) => Math.max(p - 1, 0));
    } else if (e.key === 'Enter') {
      const selected = filteredEndpoints[kbdIndex];
      if (!selected) return;
      const idx = endpoints.findIndex((e) => e.id === selected.id);
      if (idx > -1) setActiveIdx(idx);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: lighten(PRIMARYCOLOR, 0.98) }}>
      <Nav />

      <Box sx={{ flexGrow: 1, display: 'flex', overflow: 'hidden' }}>
        {/* MAIN SCROLL AREA -------------------------------------- */}
        <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, overflowY: 'auto' }}>
          {/* Header */}
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
            <Box>
              <Breadcrumbs separator={<ChevronRightIcon fontSize="small" />} sx={{ mb: 0.5 }}>
                <Typography color="text.secondary">Pentests</Typography>
                <Typography color="text.secondary">API</Typography>
                <Typography color="text.primary">Threat Model</Typography>
              </Breadcrumbs>
              <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                {activeIdx === 0 ? 'API Threat Landscape' : 'Threat Model'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ mt: { xs: 1, md: 0 } }}>
              {activeIdx !== 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<LinkIcon />}
                  onClick={() => navigator?.clipboard?.writeText(active.path)}
                >
                  Copy path
                </Button>
              )}
              <Button variant="contained" size="small" startIcon={<InfoOutlinedIcon />} onClick={() => setActiveIdx(0)}>
                Overview
              </Button>
            </Stack>
          </Stack>

          {/* KPI Cards (only on Overview) */}
          {activeIdx === 0 && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
                  <Typography variant="overline" color="text.secondary">TOTAL ENDPOINTS</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>{stats.total}</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
                  <Typography variant="overline" color="text.secondary">HIGH-RISK ENDPOINTS</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#d32f2f' }}>{stats.risky}</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>METHODS DISTRIBUTION</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {Object.entries(stats.methods).map(([m, c]) => (
                      <Chip key={m} label={`${m} · ${c}`} sx={{ bgcolor: methodColor(m), color: '#000', fontWeight: 700 }} />
                    ))}
                    {Object.keys(stats.methods).length === 0 && (
                      <Typography variant="body2" color="text.secondary">No methods detected</Typography>
                    )}
                  </Stack>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* API OVERVIEW — TABLE */}
          {activeIdx === 0 ? (
            <TableContainer component={Paper} sx={{ mb: 3, borderRadius: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><b>Attack</b></TableCell>
                    <TableCell align="center"><b>Endpoints</b></TableCell>
                    <TableCell><b>Preview</b></TableCell>
                    <TableCell sx={{ width: 160 }}><b>Risk</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {overviewRows.map((row) => (
                    <TableRow key={row.name} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.name}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={row.endpoints.length} color="primary" size="small" />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {row.endpoints.slice(0, 3).map((p) => (
                            <Chip
                              key={p}
                              label={trunc(p, 24)}
                              size="small"
                              onClick={() => goToEndpointPath(p)}
                              sx={{ cursor: 'pointer' }}
                            />
                          ))}
                          {row.endpoints.length > 3 && (
                            <Chip size="small" variant="outlined" label={`+${row.endpoints.length - 3}`} />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(100, (row.count / Math.max(1, stats.total)) * 100)}
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            /* ENDPOINT VIEW — CARDS */
            highAttacks.length > 0 && (
              <>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Likely Attack Paths</Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {highAttacks.map((atk, idx) => (
                    <Grid item xs={12} sm={6} md={4} key={idx}>
                      <Card sx={{ borderLeft: `6px solid ${scoreColor(atk.score)}`, borderRadius: 3, boxShadow: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{atk.name}</Typography>
                          <Chip label={`Score: ${atk.score}`} size="small" sx={{ mt: 0.5, background: scoreColor(atk.score), color: '#fff' }} />
                          <Typography variant="body2" sx={{ mt: 1 }}>{atk.reason}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                <Divider sx={{ mb: 3 }} />
              </>
            )
          )}

          {/* Summary */}
          <Card sx={{ bgcolor: '#fff', mb: 4, borderRadius: 3, boxShadow: 2 }}>
            <CardContent>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                {activeIdx === 0 ? 'API Overview' : 'Endpoint Summary'}
              </Typography>
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                {active.md || '_No summary available for this item._'}
              </ReactMarkdown>
            </CardContent>
          </Card>
        </Box>

        {/* ENDPOINT SIDE PANEL --------------------------------- */}
        <Paper
          elevation={3}
          sx={{
            position: 'sticky',
            top: 64,
            height: 'calc(100vh - 64px)',
            flex: '0 0 360px',
            maxWidth: 360,
            minWidth: 360,
            borderLeft: '1px solid #e0e0e0',
            bgcolor: '#ffffff',
            overflowY: 'auto',
          }}
        >
          {/* search + filter row */}
          <Box sx={{ p: 1.5, borderBottom: '1px solid #eee', position: 'sticky', top: 0, bgcolor: '#fff', zIndex: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search endpoints (path)…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setKbdIndex(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() =>
                        setMethodFilter((m) =>
                          m === 'ALL' ? 'GET' :
                          m === 'GET' ? 'POST' :
                          m === 'POST' ? 'PUT' :
                          m === 'PUT' ? 'DELETE' :
                          m === 'DELETE' ? 'PATCH' :
                          'ALL')}
                      title={`Filter method: ${methodFilter}`}
                    >
                      <FilterListIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip
                size="small"
                label={`Method: ${methodFilter}`}
                onDelete={methodFilter !== 'ALL' ? () => setMethodFilter('ALL') : undefined}
              />
              <Chip size="small" label="Tip: Click an endpoint →" icon={<ChevronRightIcon />} />
            </Stack>
          </Box>

          <List
            dense
            onKeyDown={onKeyDownList}
            subheader={
              <ListSubheader component="div" sx={{ bgcolor: '#fff', fontWeight: 800, fontSize: '1rem' }}>
                Endpoints
              </ListSubheader>
            }
          >
            {filteredEndpoints.map((ep, idx) => {
              const listIndex = filteredEndpoints.findIndex((e) => e.id === ep.id);
              const isSelected = endpoints[activeIdx]?.id === ep.id;
              const isKeyboardFocused = listIndex === kbdIndex;
              const highCount = (ep.attacks || []).filter((a) => a.score >= 0).length;

              return (
                <Tooltip key={ep.id} title={`${ep.method} ${ep.path}`} placement="left" arrow enterDelay={300}>
                  <ListItemButton
                    selected={isSelected}
                    onClick={() => setActiveIdx(endpoints.findIndex((e) => e.id === ep.id))}
                    sx={{
                      alignItems: 'flex-start',
                      py: 1.25,
                      borderLeft: isSelected ? `3px solid ${PRIMARYCOLOR}` : '3px solid transparent',
                      '&.Mui-selected': { bgcolor: lighten(PRIMARYCOLOR, 0.9) },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {ep.id === 0 ? (
                            <Chip size="small" label="OVERVIEW" variant="outlined" />
                          ) : (
                            <Chip label={ep.method} size="small" sx={{ bgcolor: methodColor(ep.method), fontWeight: 800 }} />
                          )}
                          <Typography variant="body2" sx={{ fontWeight: isSelected ? 800 : 500, wordBreak: 'break-all' }}>
                            {trunc(ep.path, 46)}
                          </Typography>
                        </Stack>
                      }
                      secondary={
                        ep.id !== 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            {highCount > 0 && (
                              <Chip label={`⚠️ ${highCount}`} size="small" sx={{ bgcolor: '#fff3e0', color: '#fb8c00', mr: 0.75 }} />
                            )}
                            <Chip
                              variant="outlined"
                              size="small"
                              label="Click to view threat model"
                              icon={<ChevronRightIcon />}
                              sx={{ borderStyle: 'dashed' }}
                            />
                          </Box>
                        )
                      }
                      sx={{ m: 0 }}
                    />
                  </ListItemButton>
                </Tooltip>
              );
            })}
          </List>
        </Paper>
      </Box>
    </Box>
  );
};

export default PentestApiThreatModel;
